import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExportOutlined,
  FileTextOutlined,
  PercentageOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  List,
  message,
  Progress,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../config/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const { Text, Title: AntTitle } = Typography;
const { RangePicker } = DatePicker;

interface AnalyticsData {
  formId: string;
  formTitle: string;
  totalResponses: number;
  responseRate: number;
  questions: QuestionAnalytics[];
}

interface QuestionAnalytics {
  id: string;
  question: string;
  type: string;
  required: boolean;
  totalAnswers: number;
  data: any;
}

const FeedbackAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateRange) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }

      const response = await api.get(`/feedback/forms/${id}/analytics`, { params });
      setAnalytics(response.data);
    } catch (error) {
      message.error(t('feedbackAnalytics.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAnalytics();
    }
  }, [id, dateRange]);

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response = await api.get(`/feedback/forms/${id}/export`, {
        params: { format },
        responseType: format === 'csv' ? 'text' : 'json',
      });

      const blob = new Blob(
        [format === 'csv' ? response.data : JSON.stringify(response.data, null, 2)],
        { type: format === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback-analytics-${id}-${dayjs().format('YYYY-MM-DD')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success(t('feedbackAnalytics.exportSuccess', { format: format.toUpperCase() }));
    } catch (error) {
      message.error(t('feedbackAnalytics.exportFailed'));
    }
  };

  const renderQuestionChart = (question: QuestionAnalytics) => {
    switch (question.type) {
      case 'RATING':
      case 'SCALE': {
        if (!question.data?.distribution) return null;

        const barData = {
          labels: Object.keys(question.data.distribution).map((k) =>
            t('feedbackAnalytics.scoreLabel', { score: k })
          ),
          datasets: [
            {
              label: t('feedbackAnalytics.datasetResponses'),
              data: Object.values(question.data.distribution) as number[],
              backgroundColor: 'rgba(75, 192, 192, 0.6)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
            },
          ],
        };

        return (
          <Bar
            data={barData}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { stepSize: 1 },
                },
              },
            }}
          />
        );
      }

      case 'YES_NO': {
        const pieData = {
          labels: [t('feedbackAnalytics.yes'), t('feedbackAnalytics.no')],
          datasets: [
            {
              data: [question.data.yes || 0, question.data.no || 0],
              backgroundColor: ['#52c41a', '#ff4d4f'],
              borderWidth: 0,
            },
          ],
        };

        return (
          <div style={{ maxWidth: 300, margin: '0 auto' }}>
            <Doughnut
              data={pieData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'bottom' },
                },
              }}
            />
          </div>
        );
      }

      case 'SINGLE_CHOICE':
      case 'MULTIPLE_CHOICE': {
        if (!question.data?.distribution) return null;

        const choiceData = {
          labels: Object.keys(question.data.distribution),
          datasets: [
            {
              label: t('feedbackAnalytics.datasetSelections'),
              data: Object.values(question.data.distribution) as number[],
              backgroundColor: 'rgba(153, 102, 255, 0.6)',
              borderColor: 'rgba(153, 102, 255, 1)',
              borderWidth: 1,
            },
          ],
        };

        return (
          <Bar
            data={choiceData}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { stepSize: 1 },
                },
              },
            }}
          />
        );
      }

      case 'TEXT': {
        if (!question.data?.wordCloud || Object.keys(question.data.wordCloud).length === 0) {
          return <Empty description={t('feedbackAnalytics.noTextResponses')} />;
        }

        const topWords = Object.entries(question.data.wordCloud)
          .slice(0, 20)
          .sort(([, a], [, b]) => (b as number) - (a as number));

        return (
          <div>
            <AntTitle level={5}>{t('feedbackAnalytics.mostCommonWords')}</AntTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {topWords.map(([word, count]) => (
                <Tag
                  key={word}
                  style={{
                    fontSize: Math.min(12 + (count as number) * 2, 24),
                    padding: `${Math.min(4 + (count as number), 8)}px ${Math.min(8 + (count as number) * 2, 16)}px`,
                  }}
                  color="blue"
                >
                  {`${word} (${count})`}
                </Tag>
              ))}
            </div>
            {question.data.samples && question.data.samples.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <AntTitle level={5}>{t('feedbackAnalytics.sampleResponses')}</AntTitle>
                <List
                  size="small"
                  dataSource={question.data.samples.slice(0, 5)}
                  renderItem={(item: string) => (
                    <List.Item>
                      <Text style={{ fontStyle: 'italic' }}>"{item}"</Text>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const renderQuestionSummary = (question: QuestionAnalytics) => {
    switch (question.type) {
      case 'RATING':
      case 'SCALE':
        return (
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title={t('feedbackAnalytics.statAverage')}
                value={question.data?.average || 0}
                precision={1}
                prefix={<StarOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={8}>
              <Statistic title={t('feedbackAnalytics.statMin')} value={question.data?.min || 0} />
            </Col>
            <Col span={8}>
              <Statistic title={t('feedbackAnalytics.statMax')} value={question.data?.max || 0} />
            </Col>
          </Row>
        );

      case 'YES_NO': {
        const total = (question.data?.yes || 0) + (question.data?.no || 0);
        const yesPercent = total > 0 ? ((question.data?.yes || 0) / total) * 100 : 0;
        return (
          <div>
            <Progress
              percent={yesPercent}
              strokeColor="#52c41a"
              format={(percent) =>
                t('feedbackAnalytics.percentYes', { percent: percent?.toFixed(0) })
              }
            />
            <Space style={{ marginTop: 8 }}>
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t('feedbackAnalytics.yesCount', { count: question.data?.yes || 0 })}
              </Tag>
              <Tag icon={<CloseCircleOutlined />} color="error">
                {t('feedbackAnalytics.noCount', { count: question.data?.no || 0 })}
              </Tag>
            </Space>
          </div>
        );
      }

      case 'SINGLE_CHOICE':
      case 'MULTIPLE_CHOICE':
        return (
          <List
            size="small"
            dataSource={question.data?.topChoices || []}
            renderItem={(item: any) => (
              <List.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{item.choice}</Text>
                  <Tag color="blue">{t('feedbackAnalytics.selections', { count: item.count })}</Tag>
                </Space>
              </List.Item>
            )}
          />
        );

      case 'TEXT':
        return (
          <div>
            <Statistic
              title={t('feedbackAnalytics.statTotalTextResponses')}
              value={question.data?.totalResponses || 0}
              prefix={<FileTextOutlined />}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!analytics) {
    return <Empty description={t('feedbackAnalytics.noAnalytics')} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 24 }} size="large">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/feedback')}>
          {t('feedbackAnalytics.backToForms')}
        </Button>

        <RangePicker
          onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
          format="YYYY-MM-DD"
        />

        <Segmented
          options={[
            { label: t('feedbackAnalytics.viewSummary'), value: 'summary' },
            { label: t('feedbackAnalytics.viewDetailed'), value: 'detailed' },
          ]}
          value={viewMode}
          onChange={(value) => setViewMode(value as 'summary' | 'detailed')}
        />

        <Space>
          <Button icon={<ExportOutlined />} onClick={() => handleExport('csv')}>
            {t('feedbackAnalytics.exportCsv')}
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => handleExport('json')}>
            {t('feedbackAnalytics.exportJson')}
          </Button>
        </Space>
      </Space>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <AntTitle level={3}>{analytics.formTitle}</AntTitle>
            <Row gutter={16} style={{ marginTop: 24 }}>
              <Col span={6}>
                <Statistic
                  title={t('feedbackAnalytics.statTotalResponses')}
                  value={analytics.totalResponses}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('feedbackAnalytics.statResponseRate')}
                  value={analytics.responseRate}
                  precision={1}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('feedbackAnalytics.statQuestions')}
                  value={analytics.questions.length}
                  prefix={<BarChartOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('feedbackAnalytics.statCompletionRate')}
                  value={
                    analytics.questions.length > 0
                      ? (analytics.questions.filter((q) => q.totalAnswers > 0).length /
                          analytics.questions.length) *
                        100
                      : 0
                  }
                  precision={1}
                  suffix="%"
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {analytics.questions.map((question, index) => (
          <Col span={viewMode === 'summary' ? 12 : 24} key={question.id}>
            <Card
              title={
                <Space>
                  <Tag>{index + 1}</Tag>
                  <Text>{question.question}</Text>
                  {question.required && <Tag color="red">{t('feedbackAnalytics.required')}</Tag>}
                  <Tag color="blue">{question.type}</Tag>
                </Space>
              }
              extra={
                <Text type="secondary">
                  {t('feedbackAnalytics.answeredOf', {
                    answered: question.totalAnswers,
                    total: analytics.totalResponses,
                  })}
                </Text>
              }
            >
              {viewMode === 'summary'
                ? renderQuestionSummary(question)
                : renderQuestionChart(question)}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default FeedbackAnalytics;
